/**
 * DashboardRenderer (P3-S2 Doc 42) — fetch widget data + render grid.
 *
 * v1 dùng CSS grid 12-col static. Drag/resize defer v1.1.
 */
import * as React from "react";

import { type DashboardSpec, dashboardsApi } from "@/api/dashboards";
import { ChartWidget } from "@/components/dashboard/widgets/ChartWidget";
import { KpiWidget } from "@/components/dashboard/widgets/KpiWidget";
import { TableWidget } from "@/components/dashboard/widgets/TableWidget";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  spec: DashboardSpec;
  /** Force refresh trigger từ parent. */
  refreshKey?: number;
}

interface WidgetState {
  loading: boolean;
  data: Record<string, unknown> | null;
  error: string | null;
}

export function DashboardRenderer({ spec, refreshKey = 0 }: Props): JSX.Element {
  const [states, setStates] = React.useState<Record<string, WidgetState>>({});

  const fetchAll = React.useCallback(async () => {
    // Mark all loading
    const initial: Record<string, WidgetState> = {};
    for (const w of spec.widgets) {
      initial[w.id] = { loading: true, data: null, error: null };
    }
    setStates(initial);

    // Fetch in parallel
    const results = await Promise.all(
      spec.widgets.map((w) =>
        dashboardsApi
          .resolveWidget(w.data_source, w.data_params, true)
          .then((r) => ({ id: w.id, ok: r.ok, data: r.data, error: r.error }))
          .catch((e: unknown) => ({
            id: w.id,
            ok: false,
            data: null,
            error: e instanceof Error ? e.message : String(e),
          })),
      ),
    );

    setStates((prev) => {
      const next = { ...prev };
      for (const r of results) {
        next[r.id] = {
          loading: false,
          data: (r.data as Record<string, unknown> | null | undefined) ?? null,
          error: r.ok ? null : r.error ?? null,
        };
      }
      return next;
    });
  }, [spec.widgets]);

  React.useEffect(() => {
    void fetchAll();
  }, [fetchAll, refreshKey]);

  if (!spec.widgets || spec.widgets.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          Dashboard chưa có widget nào.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3 auto-rows-[60px]">
      {spec.widgets.map((widget) => {
        const state = states[widget.id] ?? {
          loading: true,
          data: null,
          error: null,
        };
        const w = clamp(widget.grid.w, 1, 12);
        const h = clamp(widget.grid.h, 1, 20);
        // CSS grid: col-span / row-span. Vị trí (x,y) chỉ làm gợi ý — implicit
        // flow theo thứ tự widget hơn explicit grid-column-start (giữ đơn giản).
        // v1.1 sẽ migrate sang react-grid-layout cho drag/resize.
        return (
          <Card
            key={widget.id}
            className={cn("overflow-hidden", colSpanClass(w), rowSpanClass(h))}
          >
            <CardContent className="p-3 h-full flex flex-col min-h-0">
              {state.error ? (
                <div className="h-full flex flex-col">
                  <div className="text-xs font-semibold mb-1 text-destructive">
                    {widget.title}
                  </div>
                  <div className="text-[10px] text-destructive/80 bg-destructive/5 border border-destructive/20 rounded px-2 py-1.5">
                    {state.error}
                  </div>
                </div>
              ) : widget.type === "kpi" ? (
                <KpiWidget
                  widget={widget}
                  data={state.data}
                  loading={state.loading}
                />
              ) : widget.type === "table" ? (
                <TableWidget
                  widget={widget}
                  data={state.data}
                  loading={state.loading}
                />
              ) : (
                <ChartWidget
                  widget={widget}
                  data={state.data}
                  loading={state.loading}
                />
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

const COL_SPAN_CLASSES: Record<number, string> = {
  1: "col-span-12 sm:col-span-1",
  2: "col-span-12 sm:col-span-2",
  3: "col-span-12 sm:col-span-3",
  4: "col-span-12 sm:col-span-4",
  5: "col-span-12 sm:col-span-5",
  6: "col-span-12 sm:col-span-6",
  7: "col-span-12 sm:col-span-7",
  8: "col-span-12 sm:col-span-8",
  9: "col-span-12 sm:col-span-9",
  10: "col-span-12 sm:col-span-10",
  11: "col-span-12 sm:col-span-11",
  12: "col-span-12",
};

function colSpanClass(w: number): string {
  return COL_SPAN_CLASSES[w] ?? "col-span-12";
}

const ROW_SPAN_CLASSES: Record<number, string> = {
  1: "row-span-1",
  2: "row-span-2",
  3: "row-span-3",
  4: "row-span-4",
  5: "row-span-5",
  6: "row-span-6",
  7: "row-span-7",
  8: "row-span-8",
  9: "row-span-9",
  10: "row-span-10",
};

function rowSpanClass(h: number): string {
  return ROW_SPAN_CLASSES[h] ?? "row-span-3";
}
